import { useEffect, useMemo, useState } from 'react';

import type { Commit } from '@/pages/developer/types';
import { isPullRequestCommit } from '@/pages/developer/githubUtils';

export type CommitHistoryFilter = 'all' | 'pulls';

export type UseCommitHistoryFiltersResult = {
  searchQuery: string;
  setSearchQuery: (next: string) => void;

  commitFilter: CommitHistoryFilter;
  setCommitFilter: (next: CommitHistoryFilter) => void;

  filteredCommits: Commit[];
  filteredCommitShas: string[];
};

const STORAGE_KEY = 'developerCommitFilter';

const loadStoredFilter = (): CommitHistoryFilter => {
  if (typeof window === 'undefined') return 'all';
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return stored === 'pulls' ? 'pulls' : 'all';
  } catch {
    return 'all';
  }
};

export const useCommitHistoryFilters = (commits: Commit[]): UseCommitHistoryFiltersResult => {
  const [searchQuery, setSearchQuery] = useState('');
  const [commitFilter, setCommitFilter] = useState<CommitHistoryFilter>(() => loadStoredFilter());

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(STORAGE_KEY, commitFilter);
    } catch {
      // ignore
    }
  }, [commitFilter]);

  const filteredCommits = useMemo(() => {
    const base = commitFilter === 'pulls' ? commits.filter((commit) => isPullRequestCommit(commit)) : commits;

    const trimmed = searchQuery.trim();
    if (!trimmed) return base;

    const tokens = trimmed
      .toLowerCase()
      .split(/\s+/)
      .filter(Boolean);

    if (tokens.length === 0) return base;

    return base.filter((commit) => {
      const when = new Date(commit.date);
      const safeDate = Number.isNaN(when.getTime()) ? '' : when.toISOString().slice(0, 10);
      const haystack = [commit.sha, commit.message, commit.author, commit.date, safeDate, commit.milestoneTitle ?? '']
        .join(' ')
        .toLowerCase();

      return tokens.every((token) => haystack.includes(token));
    });
  }, [commitFilter, commits, searchQuery]);

  const filteredCommitShas = useMemo(() => filteredCommits.map((commit) => commit.sha), [filteredCommits]);

  return {
    searchQuery,
    setSearchQuery,
    commitFilter,
    setCommitFilter,
    filteredCommits,
    filteredCommitShas,
  };
};
